import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LabourMobilizationService } from '../../shared/services/labour-mobilization.service';
import { ToastrMsgService } from '../../shared/services/shared-services/toastr.service';
import { ApiService } from '../../shared/services/api.service';

@Component({
  selector: 'app-mobilization-plan-form',
  templateUrl: './mobilization-plan-form.component.html',
  styleUrls: ['./mobilization-plan-form.component.css']
})
export class MobilizationPlanFormComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  planForm!: FormGroup;
  planId: number | null = null;
  isEditMode = false;
  loading = false;
  projects: any[] = [];
  labourCategories: Array<{ key: string; label: string; visible: boolean }> = [];
  defaultCategories = [
    { key: 'carpenders', label: 'Carpenters', visible: true },
    { key: 'carpenders_helpers', label: 'Carpenter Helpers', visible: true },
    { key: 'rein_fitters', label: 'Reinforcement Fitters', visible: true },
    { key: 'rein_fitters_helpers', label: 'Fitter Helpers', visible: true },
    { key: 'masons', label: 'Masons', visible: true },
    { key: 'masons_helpers', label: 'Mason Helpers', visible: true }
  ];
  showCategoryManager = false;
  newCategoryLabel = '';
  editingCategoryKey: string | null = null;
  editingCategoryLabel = '';

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private labourService: LabourMobilizationService,
    private apiService: ApiService,
    private toastrMsgService: ToastrMsgService
  ) {
    this.loadCategories();
    this.initializeForm();
  }

  loadCategories(): void {
    const savedCategories = localStorage.getItem('labour_mobilization_categories');
    if (savedCategories) {
      try {
        this.labourCategories = JSON.parse(savedCategories);
      } catch (e) {
        this.labourCategories = [...this.defaultCategories];
      }
    } else {
      this.labourCategories = [...this.defaultCategories];
    }
  }

  saveCategories(): void {
    localStorage.setItem('labour_mobilization_categories', JSON.stringify(this.labourCategories));
  }

  addCategory(): void {
    const label = this.newCategoryLabel.trim();
    if (!label) return;
    const newKey = label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (!newKey || this.labourCategories.some(cat => cat.key === newKey)) return;
    this.labourCategories.push({ key: newKey, label, visible: true });
    this.planForm.addControl(newKey, this.fb.control(0, [Validators.min(0)]));
    this.newCategoryLabel = '';
    this.saveCategories();
  }

  removeCategory(key: string): void {
    if (this.labourCategories.length <= 1) return;
    const index = this.labourCategories.findIndex(cat => cat.key === key);
    if (index > -1) {
      this.labourCategories.splice(index, 1);
      this.planForm.removeControl(key);
      this.saveCategories();
    }
  }

  startEditCategory(key: string): void {
    const cat = this.labourCategories.find(c => c.key === key);
    if (cat) {
      this.editingCategoryKey = key;
      this.editingCategoryLabel = cat.label;
    }
  }

  saveCategoryEdit(): void {
    if (!this.editingCategoryKey) return;
    const cat = this.labourCategories.find(c => c.key === this.editingCategoryKey);
    if (cat && this.editingCategoryLabel.trim()) {
      cat.label = this.editingCategoryLabel.trim();
      this.saveCategories();
    }
    this.cancelCategoryEdit();
  }

  cancelCategoryEdit(): void {
    this.editingCategoryKey = null;
    this.editingCategoryLabel = '';
  }

  ngOnInit(): void {
    this.loadProjects();
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.planId = +params['id'];
        this.isEditMode = true;
        this.loadPlan();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeForm(): void {
    const formConfig: any = {
      project_id: ['', Validators.required],
      plan_date: [new Date().toISOString().split('T')[0], Validators.required],
      committed_to_increase: [0, [Validators.min(0)]],
      expected_total_strength: [0, [Validators.min(0)]],
      net_increase: [0],
      notes: ['']
    };
    this.labourCategories.forEach(cat => {
      formConfig[cat.key] = [0, [Validators.min(0)]];
    });
    this.planForm = this.fb.group(formConfig);
  }

  loadProjects(): void {
    this.apiService.getProjectCommonAll({ limit: 1000 }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => this.projects = res.data || res || [],
      error: (err) => console.error('Error loading projects:', err)
    });
  }

  loadPlan(): void {
    if (!this.planId) return;
    this.loading = true;
    this.labourService.getMobilizationPlanById(this.planId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        const data = response.data as any;
        const categoryData = data.category_data as Record<string, number> | undefined;
        const hasCategoryData = categoryData && typeof categoryData === 'object' && Object.keys(categoryData).length > 0;

        if (hasCategoryData) {
          // Merge any new keys from response into labourCategories
          Object.keys(categoryData).forEach(key => {
            if (!this.labourCategories.some(c => c.key === key)) {
              this.labourCategories.push({ key, label: this.formatLabel(key), visible: true });
              this.planForm.addControl(key, this.fb.control(categoryData[key] ?? 0, [Validators.min(0)]));
            }
          });
          this.saveCategories();
          const patchValue: any = { project_id: data.project_id, plan_date: data.plan_date, committed_to_increase: data.committed_to_increase ?? 0, expected_total_strength: data.expected_total_strength ?? 0, net_increase: data.net_increase ?? 0, notes: data.notes ?? '' };
          this.labourCategories.forEach(cat => {
            patchValue[cat.key] = categoryData[cat.key] ?? 0;
          });
          this.planForm.patchValue(patchValue);
        } else {
          // Backward compat: map fixed columns to form
          const patchValue: any = { ...data };
          this.labourCategories.forEach(cat => {
            if (data[cat.key] !== undefined) patchValue[cat.key] = data[cat.key];
          });
          this.planForm.patchValue(patchValue);
        }
        this.loading = false;
      },
      error: (err) => {
        this.toastrMsgService.error(err.error?.message || 'Failed to load plan');
        this.loading = false;
      }
    });
  }

  formatLabel(key: string): string {
    return key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }

  calculateTotalAvailable(): number {
    let total = 0;
    this.labourCategories.forEach(cat => {
      const val = this.planForm.get(cat.key)?.value;
      total += (val || 0);
    });
    return total;
  }

  calculateShortage(): number {
    const expected = this.planForm.get('expected_total_strength')?.value || 0;
    return expected - this.calculateTotalAvailable();
  }

  onSubmit(): void {
    if (this.planForm.valid) {
      this.loading = true;
      const formValue = this.planForm.value;
      const category_data: Record<string, number> = {};
      this.labourCategories.forEach(cat => {
        const val = formValue[cat.key];
        if (val !== undefined && val !== null) category_data[cat.key] = Number(val) || 0;
      });
      const planData = {
        project_id: formValue.project_id,
        plan_date: formValue.plan_date,
        committed_to_increase: formValue.committed_to_increase ?? 0,
        expected_total_strength: formValue.expected_total_strength ?? 0,
        net_increase: formValue.net_increase ?? 0,
        notes: formValue.notes ?? '',
        category_data,
        carpenders: 0,
        carpenders_helpers: 0,
        rein_fitters: 0,
        rein_fitters_helpers: 0,
        masons: 0,
        masons_helpers: 0
      };
      const operation = this.isEditMode
        ? this.labourService.updateMobilizationPlan(this.planId!, planData)
        : this.labourService.createMobilizationPlan(planData);
      
      operation.pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.toastrMsgService.success(this.isEditMode ? 'Plan updated successfully' : 'Plan created successfully');
          this.router.navigate(['/home/labour-mobilization/mobilization-plans']);
        },
        error: (err: any) => {
          this.toastrMsgService.error(err.error?.message || 'Failed to save plan');
          this.loading = false;
        }
      });
    } else {
      this.toastrMsgService.error('Please fill all required fields');
    }
  }

  cancel(): void {
    this.router.navigate(['/home/labour-mobilization/mobilization-plans']);
  }
}

